import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BACKEND_URL, DEFAULT_BRAND } from "../config/brand";
import { DEFAULT_TEXTS as BASE_DEFAULT_TEXTS } from "../config/contentDefaults";
import { fetchContent, saveContentText, saveContentTexts } from "../lib/api";

const DEFAULT_TEXTS = {
  ...BASE_DEFAULT_TEXTS,
  "home.hero.title": DEFAULT_BRAND.hero_title || BASE_DEFAULT_TEXTS["home.hero.title"],
  "home.hero.subtitle": DEFAULT_BRAND.hero_subtitle || BASE_DEFAULT_TEXTS["home.hero.subtitle"],
};

const ContentContext = createContext({
  texts: DEFAULT_TEXTS,
  loading: true,
  getText: (key, fallback) => fallback ?? DEFAULT_TEXTS[key] ?? "",
  setLocalText: () => {},
  saveText: async () => {},
  saveTexts: async () => {},
  refresh: async () => {},
});

/** Construit un map texts depuis /api/settings si /api/content est indisponible. */
function textsFromSettings(settings) {
  return {
    ...DEFAULT_TEXTS,
    "home.hero.title": settings.hero_title || DEFAULT_TEXTS["home.hero.title"],
    "home.hero.subtitle": settings.hero_subtitle || DEFAULT_TEXTS["home.hero.subtitle"],
  };
}

/** Normalise la réponse GET /api/content vers un map { key: string }. */
function textsFromContentResponse(data) {
  if (!data || typeof data !== "object") return null;

  if (data.texts && typeof data.texts === "object" && !Array.isArray(data.texts)) {
    return data.texts;
  }

  // Compat anciennes réponses { items: { key: { value } } } ou { items: [...] }
  const items = data.items;
  if (items && typeof items === "object") {
    const out = {};
    const list = Array.isArray(items) ? items : Object.values(items);
    for (const item of list) {
      if (!item?.key) continue;
      if (typeof item.value === "string" && item.value !== "") {
        out[item.key] = item.value;
      }
    }
    return out;
  }

  return null;
}

async function loadContentTexts() {
  if (!BACKEND_URL) return DEFAULT_TEXTS;

  try {
    const data = await fetchContent();
    const fromApi = textsFromContentResponse(data);
    if (fromApi) {
      return { ...DEFAULT_TEXTS, ...fromApi };
    }
  } catch {
    // fallback settings
  }

  try {
    const res = await fetch(`${BACKEND_URL}/api/settings`);
    if (res.ok) {
      const settings = await res.json();
      return textsFromSettings(settings);
    }
  } catch {
    // defaults
  }

  return DEFAULT_TEXTS;
}

export function ContentProvider({ children }) {
  const [texts, setTexts] = useState(DEFAULT_TEXTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await loadContentTexts();
      setTexts(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const next = await loadContentTexts();
      if (!cancelled) {
        setTexts(next);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getText = useCallback(
    (key, fallback) => {
      if (texts[key] != null && texts[key] !== "") return texts[key];
      if (fallback != null) return fallback;
      return DEFAULT_TEXTS[key] ?? "";
    },
    [texts]
  );

  const setLocalText = useCallback((key, value) => {
    setTexts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveTexts = useCallback(async (patch) => {
    const updated = await saveContentTexts(patch);
    setTexts((prev) => {
      const next = { ...prev, ...patch };
      // Fusionne les overrides non vides renvoyés par l'API
      if (updated?.texts) {
        for (const [k, v] of Object.entries(updated.texts)) {
          if (typeof v === "string" && v !== "") next[k] = v;
        }
      }
      return next;
    });
    return updated;
  }, []);

  const saveText = useCallback(async (key, value) => {
    const updated = await saveContentText(key, value);
    setTexts((prev) => ({ ...prev, [key]: value }));
    return updated;
  }, []);

  const value = useMemo(
    () => ({
      texts,
      loading,
      getText,
      setLocalText,
      saveText,
      saveTexts,
      refresh,
    }),
    [texts, loading, getText, setLocalText, saveText, saveTexts, refresh]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  return useContext(ContentContext);
}

export { DEFAULT_TEXTS };
